import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import textServices from 'services/text-services'
import { Question, Survey, SurveyResponse } from 'contexts/survey-context/survey.interface'
import usePostalCodeLookup from 'hooks/use-postal-code-lookup'
import { useSession } from 'contexts/session-context'
import SurveyService from 'services/survey.services'
import { NEIGHBARS_CP_QUESTION_ID } from '../constants'

interface SurveyFormSectionProps {
  survey: Survey
  onSubmit: (answers: SurveyResponse[]) => Promise<boolean>
}

const normalizeText = (value: string) =>
  value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

// The composite postal-code block replaces the plain input when the survey
// question matches by official id or, as fallback, by its text.
const isPostalCodeQuestion = (question: Question) => {
  if (NEIGHBARS_CP_QUESTION_ID !== null) return question.questionId === NEIGHBARS_CP_QUESTION_ID
  return normalizeText(question.question ?? '').includes('codigo postal')
}

// Only the "retos" question keeps the large multiline field (design 26:609);
// every other TextBox renders as a single-line input.
const isLongTextQuestion = (question: Question) =>
  normalizeText(question.question ?? '').includes('reto')

// Placeholder: the survey subtitle when defined, otherwise a per-question
// default matching the design copy.
const TEXT_PLACEHOLDERS: { match: string; text: string }[] = [
  { match: 'nombre', text: 'Ingresa tu nombre aquí' },
  { match: 'celular', text: 'Ingresa tu número de celular aquí' },
  { match: 'correo', text: 'tu@mail.com' },
  { match: 'habitantes', text: 'Ingresa la cantidad' },
  { match: 'reto', text: 'Menciona los retos que se han encontrado para una buena socialización entre los vecinos...' }
]

const getTextPlaceholder = (question: Question) => {
  if (question.subtitle) return question.subtitle
  const normalized = normalizeText(question.question ?? '')
  const match = TEXT_PLACEHOLDERS.find((entry) => normalized.includes(entry.match))
  return match?.text ?? 'Escribe tu respuesta aquí'
}

// Image questions come from the survey admin with their own type; they render
// the upload dropzone (Figma 26:609) instead of a regular field.
const isImageQuestion = (question: Question) => question.type === 'Image'

// The validate payload exposes no required/optional flag today (verified
// against the local backend), so optionality falls back to the question text;
// if the backend ever ships a flag, it takes precedence.
const isOptionalQuestion = (question: Question) => {
  const flags = question as unknown as { isOptional?: boolean; isRequired?: boolean; required?: boolean }
  if (typeof flags.isOptional === 'boolean') return flags.isOptional
  if (typeof flags.isRequired === 'boolean') return !flags.isRequired
  if (typeof flags.required === 'boolean') return !flags.required
  return normalizeText(question.question ?? '').includes('opcional')
}

// Empty optional text answers are still sent with this explicit marker
const OPTIONAL_EMPTY_ANSWER = 'No completo'

const IMAGE_MIN_WIDTH = 1280
const IMAGE_MIN_HEIGHT = 720
const IMAGE_MAX_BYTES = 2 * 1024 * 1024

// Client-side validation before any upload request: format, weight and
// minimum dimensions (read through an offscreen Image).
const validateImageFile = (file: File) =>
  new Promise<string | null>((resolve) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      resolve('La imagen debe ser JPG o PNG.')
      return
    }
    if (file.size > IMAGE_MAX_BYTES) {
      resolve('La imagen supera el peso máximo de 2 MB.')
      return
    }
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(
        image.width >= IMAGE_MIN_WIDTH && image.height >= IMAGE_MIN_HEIGHT
          ? null
          : 'La imagen debe medir al menos 1280 x 720 px.'
      )
    }
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve('No pudimos leer la imagen. Intenta con otro archivo.')
    }
    image.src = objectUrl
  })

interface ImageUploadState {
  // Selected file, kept locally: the actual upload is deferred to submit so
  // picking files cannot generate endless uploads.
  file: File | null
  previewUrl: string | null
  fileName: string | null
  // URL returned by the media endpoint for the CURRENT file (upload cache:
  // a retry after a failed answer does not re-upload the same file).
  uploadedUrl: string | null
  uploading: boolean
  error: string | null
}

const EMPTY_IMAGE_UPLOAD: ImageUploadState = {
  file: null,
  previewUrl: null,
  fileName: null,
  uploadedUrl: null,
  uploading: false,
  error: null
}

interface ImageUploadFieldProps {
  upload: ImageUploadState
  onSelect: (file: File) => void
  onRemove: () => void
}

const ImageUploadField = ({ upload, onSelect, onRemove }: ImageUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="neighbars__form-field">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) onSelect(file)
          event.target.value = ''
        }}
      />

      {upload.previewUrl ? (
        <div className="neighbars__form-upload-preview">
          <img src={upload.previewUrl} alt={upload.fileName ?? ''} />
          <div className="neighbars__form-upload-preview-info">
            <p>{upload.fileName}</p>
            <div className="neighbars__form-upload-preview-actions">
              <button type="button" disabled={upload.uploading} onClick={() => inputRef.current?.click()}>
                {textServices.findByText('neighbars/form/upload/replace', 'Reemplazar')}
              </button>
              <button type="button" disabled={upload.uploading} onClick={onRemove}>
                {textServices.findByText('neighbars/form/upload/remove', 'Quitar')}
              </button>
            </div>
            {upload.uploading && (
              <p className="neighbars__form-upload-status">
                {textServices.findByText('neighbars/form/upload/uploading', 'Subiendo imagen...')}
              </p>
            )}
            {upload.error && <p className="neighbars__form-upload-error">{upload.error}</p>}
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="neighbars__form-upload"
          disabled={upload.uploading}
          onClick={() => inputRef.current?.click()}
        >
          <span className="neighbars__form-upload-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="#fff" strokeWidth="2" />
              <circle cx="9" cy="9" r="2" fill="#fff" />
              <path d="M5 19l5-6 3.5 4 2.5-3 3 5" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
            </svg>
          </span>
          <p className="neighbars__form-upload-text">
            {textServices.findByText('neighbars/form/upload/text', 'Agrega fotos del lugar aquí o')}{' '}
            <span>{textServices.findByText('neighbars/form/upload/browse', 'desde tus archivos')}</span>
          </p>
          <p className="neighbars__form-upload-caption">
            {textServices.findByText(
              'neighbars/form/upload/caption',
              'MÍNIMO 1280 x 720 px • JPG O PNG • MAX. PESO 2 MB'
            )}
          </p>
          {upload.error && <p className="neighbars__form-upload-error">{upload.error}</p>}
        </button>
      )}
    </div>
  )
}

interface PostalCodeFieldProps {
  question: Question
  lookup: ReturnType<typeof usePostalCodeLookup>
}

const PostalCodeField = ({ question, lookup }: PostalCodeFieldProps) => {
  const {
    postalCode,
    setPostalCode,
    neighborhoods,
    selectedNeighborhood,
    setSelectedNeighborhood,
    municipality,
    state,
    hasError
  } = lookup

  return (
    <div className="neighbars__form-field neighbars__form-composite">
      <div className="neighbars__form-composite-item">
        <label className="neighbars__form-label">
          {textServices.findByText('neighbars/form/cp/label', 'CP')}
        </label>
        <input
          className={`neighbars__form-input ${hasError ? 'neighbars__form-input--invalid' : ''}`}
          type="number"
          maxLength={8}
          placeholder={textServices.findByText('neighbars/form/cp/placeholder', 'Ingresa tu código postal aquí') as string}
          value={postalCode}
          onChange={(event) => setPostalCode(event.target.value)}
        />
      </div>

      <div className="neighbars__form-composite-item">
        <label className="neighbars__form-label">
          {textServices.findByText('neighbars/form/cp/neighborhood', 'TU COLONIA')}
        </label>
        <div className="neighbars__form-select">
          <select
            disabled={!neighborhoods.length}
            value={selectedNeighborhood?.id ?? ''}
            onChange={(event) => {
              const neighborhood = neighborhoods.find(({ id }) => id === Number(event.target.value)) ?? null
              setSelectedNeighborhood(neighborhood)
            }}
          >
            <option value="" disabled>
              {textServices.findByText('neighbars/form/cp/neighborhoodPlaceholder', 'Seleccionar colonia') as string}
            </option>
            {neighborhoods.map(({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <input
        className="neighbars__form-input neighbars__form-input--readonly"
        placeholder={textServices.findByText('neighbars/form/cp/municipality', 'Municipio') as string}
        readOnly
        disabled={!municipality}
        value={municipality}
      />
      <input
        className="neighbars__form-input neighbars__form-input--readonly"
        placeholder={textServices.findByText('neighbars/form/cp/state', 'Estado') as string}
        readOnly
        disabled={!state}
        value={state}
      />
      {question.subtitle ? <p className="neighbars__form-sublabel">{question.subtitle}</p> : null}
    </div>
  )
}

interface QuestionFieldProps {
  question: Question
  selected: number[]
  textValue: string
  onOptionsChange: (optionIds: number[]) => void
  onTextChange: (value: string) => void
}

const QuestionField = ({ question, selected, textValue, onOptionsChange, onTextChange }: QuestionFieldProps) => {
  const { question: label, subtitle, type, options, amountAwnsers } = question
  const isMultiple = type === 'Normal' && amountAwnsers > 1

  const toggleOption = (optionId: number) => {
    if (isMultiple || type === 'Checkbox') {
      const next = selected.includes(optionId)
        ? selected.filter((id) => id !== optionId)
        : [...selected, optionId]
      onOptionsChange(isMultiple ? next.slice(-amountAwnsers) : next)
      return
    }
    onOptionsChange([optionId])
  }

  return (
    <div className="neighbars__form-field">
      <label className="neighbars__form-label">{label}</label>
      {subtitle && type !== 'TextBox' ? <p className="neighbars__form-sublabel">{subtitle}</p> : null}

      {type === 'TextBox' &&
        (isLongTextQuestion(question) ? (
          <textarea
            className="neighbars__form-textarea neighbars__form-textarea--large"
            value={textValue}
            placeholder={getTextPlaceholder(question)}
            onChange={(event) => onTextChange(event.target.value)}
          />
        ) : (
          <input
            className="neighbars__form-input"
            type="text"
            value={textValue}
            placeholder={getTextPlaceholder(question)}
            onChange={(event) => onTextChange(event.target.value)}
          />
        ))}

      {type === 'Dropdown' && (
        <div className="neighbars__form-select">
          <select
            value={selected[0] ?? ''}
            onChange={(event) => onOptionsChange(event.target.value === '' ? [] : [Number(event.target.value)])}
          >
            <option value="" disabled>
              {textServices.findByText('neighbars/form/selectPlaceholder', 'Selecciona una opción') as string}
            </option>
            {options?.map(({ optionId, option }) => (
              <option key={optionId} value={optionId}>
                {option}
              </option>
            ))}
          </select>
        </div>
      )}

      {(type === 'Normal' || type === 'Checkbox' || type === 'RadioButton') && (
        <div className="neighbars__form-options">
          {options?.map(({ optionId, option }) => (
            <label key={optionId} className="neighbars__form-option">
              <input
                type={isMultiple || type === 'Checkbox' ? 'checkbox' : 'radio'}
                name={`question-${question.questionId}`}
                checked={selected.includes(optionId)}
                onChange={() => toggleOption(optionId)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

const SurveyFormSection = ({ survey, onSubmit }: SurveyFormSectionProps) => {
  const [{ data: session }] = useSession()
  const [selected, setSelected] = useState<Record<number, number[]>>({})
  const [textValues, setTextValues] = useState<Record<number, string>>({})
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [sending, setSending] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [imageUpload, setImageUpload] = useState<ImageUploadState>(EMPTY_IMAGE_UPLOAD)
  const postalCodeLookup = usePostalCodeLookup()

  // The image question is conditional on the common-area checkbox: hidden and
  // not required while unchecked, required (uploaded) while checked.
  const commonAreaQuestion = survey.questions?.find((question) => question.type === 'Checkbox')
  const commonAreaChecked = commonAreaQuestion
    ? (selected[commonAreaQuestion.questionId] ?? []).length > 0
    : true

  const clearImagePreview = (state: ImageUploadState) => {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl)
  }

  // Selecting a file only validates and previews it locally; the upload
  // itself is deferred to submit (prevents unlimited uploads without sending).
  const handleImageSelect = async (file: File) => {
    const validationError = await validateImageFile(file)
    setImageUpload((state) => {
      clearImagePreview(state)
      if (validationError) return { ...EMPTY_IMAGE_UPLOAD, error: validationError }
      return {
        file,
        previewUrl: URL.createObjectURL(file),
        fileName: file.name,
        uploadedUrl: null,
        uploading: false,
        error: null
      }
    })
  }

  const handleImageRemove = () => {
    setImageUpload((state) => {
      clearImagePreview(state)
      return EMPTY_IMAGE_UPLOAD
    })
  }

  // Uploads the selected file during submit; the returned URL is cached per
  // file so retrying a failed answer does not upload the image again.
  const uploadSelectedImage = async (): Promise<string | null> => {
    if (imageUpload.uploadedUrl) return imageUpload.uploadedUrl
    if (!imageUpload.file) return null
    setImageUpload((state) => ({ ...state, uploading: true, error: null }))
    try {
      const response = await SurveyService.uploadMedia({ surveyId: survey.id, file: imageUpload.file, session })
      const url = response?.data?.url ?? null
      setImageUpload((state) => ({
        ...state,
        uploadedUrl: url,
        uploading: false,
        error: url ? null : 'No pudimos subir la imagen. Intenta nuevamente.'
      }))
      return url
    } catch {
      setImageUpload((state) => ({
        ...state,
        uploading: false,
        error: 'No pudimos subir la imagen. Intenta nuevamente.'
      }))
      return null
    }
  }

  const isAnswered = (question: Question) => {
    if (isPostalCodeQuestion(question)) return postalCodeLookup.selectedNeighborhood !== null
    // Common-area checkbox is optional: it never blocks completeness
    if (question.type === 'Checkbox') return true
    // The image is required only while the common-area checkbox is checked;
    // a locally selected valid file is enough (it uploads on submit)
    if (isImageQuestion(question)) return commonAreaChecked ? imageUpload.file !== null : true
    // Optional questions never block completeness either
    if (isOptionalQuestion(question)) return true
    if (question.type === 'TextBox') return !!textValues[question.questionId]?.trim()
    return (selected[question.questionId] ?? []).length > 0
  }

  const canSubmit = termsAccepted && !sending && survey.questions?.every(isAnswered)

  // Two independent columns on desktop (Figma 26:609): identity fields plus
  // the postal-code block on the left, the remaining questions on the right.
  // Mobile flattens them back into one flow (display: contents). The image
  // question renders right below the common-area checkbox (design order) and
  // only while the checkbox is checked.
  const questions = [...(survey.questions ?? [])]
  const imageIndex = questions.findIndex(isImageQuestion)
  if (imageIndex >= 0 && commonAreaQuestion) {
    const [imageQuestion] = questions.splice(imageIndex, 1)
    const checkboxIndex = questions.findIndex(
      (question) => question.questionId === commonAreaQuestion.questionId
    )
    questions.splice(checkboxIndex + 1, 0, imageQuestion)
  }
  const visibleQuestions = questions.filter(
    (question) => !isImageQuestion(question) || commonAreaChecked
  )
  const cpIndex = visibleQuestions.findIndex(isPostalCodeQuestion)
  const splitIndex = cpIndex >= 0 ? cpIndex + 1 : Math.ceil(visibleQuestions.length / 2)
  const columns = [visibleQuestions.slice(0, splitIndex), visibleQuestions.slice(splitIndex)].filter(
    (column) => column.length > 0
  )

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSending(true)
    setSubmitError(false)

    // The image uploads now, as part of the submit; a failed upload aborts
    // the whole submission (the answer PUT never fires) and allows retrying.
    const hasImageQuestion = survey.questions.some(isImageQuestion)
    let imageUrl: string | null = null
    if (hasImageQuestion && commonAreaChecked) {
      imageUrl = await uploadSelectedImage()
      if (!imageUrl) {
        setSending(false)
        return
      }
    }

    const answers: SurveyResponse[] = survey.questions
      // The image answer is discarded while the common-area checkbox is off;
      // the checkbox itself is always sent (explicit optionIds, empty = "no").
      .filter((question) => !isImageQuestion(question) || commonAreaChecked)
      .map((question) => {
        if (isPostalCodeQuestion(question)) {
          // Same shape as the survey StepCP answer: neighborhood id as text,
          // first option id (when the question defines options).
          const firstOptionId = question.options?.[0]?.optionId
          return {
            questionId: question.questionId,
            optionIds: firstOptionId !== undefined ? [firstOptionId] : [],
            textValue: String(postalCodeLookup.selectedNeighborhood?.id)
          }
        }
        if (isImageQuestion(question)) {
          const firstOptionId = question.options?.[0]?.optionId
          return {
            questionId: question.questionId,
            optionIds: firstOptionId !== undefined ? [firstOptionId] : [],
            textValue: imageUrl
          }
        }
        // Empty optional text questions are never omitted: they travel with
        // the explicit "No completo" marker instead.
        const trimmedText = textValues[question.questionId]?.trim()
        const emptyOptionalText =
          question.type === 'TextBox' && isOptionalQuestion(question) && !trimmedText
        return {
          questionId: question.questionId,
          optionIds: selected[question.questionId] ?? [],
          textValue: emptyOptionalText ? OPTIONAL_EMPTY_ANSWER : trimmedText || null
        }
      }) as SurveyResponse[]
    const success = await onSubmit(answers)
    if (!success) setSubmitError(true)
    setSending(false)
  }

  return (
    <section className="neighbars__form" id="neighbars-form">
      <div className="neighbars__form-panel">
        <span className="neighbars__form-tag">
          {textServices.findByText('neighbars/form/tag', 'FORMULARIO')}
        </span>
        <h2 className="neighbars__form-title">
          {textServices.findByText('neighbars/form/title', '¡Participa ahora!')}
        </h2>
        <p className="neighbars__form-subtitle">
          {textServices.findByText(
            'neighbars/form/subtitle',
            'Completa el formulario con tus datos de contacto y cuéntanos tu historia.'
          )}
        </p>

        <div className="neighbars__form-fields">
          {columns.map((columnQuestions, columnIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={columnIndex} className="neighbars__form-column">
              {columnQuestions.map((question) =>
                isPostalCodeQuestion(question) ? (
                  <PostalCodeField key={question.questionId} question={question} lookup={postalCodeLookup} />
                ) : isImageQuestion(question) ? (
                  <ImageUploadField
                    key={question.questionId}
                    upload={imageUpload}
                    onSelect={handleImageSelect}
                    onRemove={handleImageRemove}
                  />
                ) : (
                  <QuestionField
                    key={question.questionId}
                    question={question}
                    selected={selected[question.questionId] ?? []}
                    textValue={textValues[question.questionId] ?? ''}
                    onOptionsChange={(optionIds) =>
                      setSelected((state) => ({ ...state, [question.questionId]: optionIds }))
                    }
                    onTextChange={(value) =>
                      setTextValues((state) => ({ ...state, [question.questionId]: value }))
                    }
                  />
                )
              )}
            </div>
          ))}
        </div>

        <label className="neighbars__form-option neighbars__form-terms">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(event) => setTermsAccepted(event.target.checked)}
          />
          <span>
            {textServices.findByText('neighbars/form/termsPrefix', 'Acepto los')}{' '}
            <Link to="/terms-and-conditions" className="underline">
              {textServices.findByText('neighbars/form/termsLink', 'Términos y Condiciones')}
            </Link>
            .
          </span>
        </label>

        {submitError && (
          <p className="neighbars__form-error">
            {textServices.findByText(
              'neighbars/form/error',
              'No pudimos enviar tu formulario. Intenta nuevamente.'
            )}
          </p>
        )}

        <button
          type="button"
          className="home-v3__button home-v3__button--blue neighbars__form-submit"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {textServices.findByText('neighbars/form/submit', 'Enviar formulario')}
        </button>
      </div>
    </section>
  )
}

export default SurveyFormSection
